import PropTypes from 'prop-types'
import React, { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Badge } from '../../../components/Badge'
import { Table } from '../../../components/Table'
import SecurityPostureSecureScore from '../../SecurityPosture/SecureScore/SecureScore'
import { formatComplianceAuditTemplates, getComplianceAuditStatusBadgeVariant } from '../utils'
import './ComplianceAuditTable.module.scss'
import { getPostureRationale, getPostureStatusDetails } from '../../SecurityPosture/utils'

const DEFAULT_VISIBLE_COLUMN_IDS = ['statusDetails', 'categories', 'assessments', 'secureScore']

const propTypes = {
  /** Rows pre-formatted for PDF/export (see complianceAuditExport.js). */
  tableData: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  /** Hides filters/sort and renders a read-only table for PDF reports. */
  print: PropTypes.bool.isRequired,
  /** Optional column visibility config from the compliance audit customize menu. */
  columnConfig: PropTypes.shape({
    visibleColumnIds: PropTypes.arrayOf(PropTypes.string),
    notesDisplayMode: PropTypes.string,
    showComplianceColumn: PropTypes.bool,
    complianceColumnLabel: PropTypes.string,
  }),
}

const formatNotesCell = (notes, notesDisplayMode, intl) => {
  if (!Array.isArray(notes) || !notes.length) {
    return null
  }

  const latest = [...notes].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0]
  if (!latest) {
    return null
  }

  if (notesDisplayMode === 'statusOnly') {
    if (latest.status === 'custom' && latest.customStatus) {
      return latest.customStatus
    }
    const messageId = {
      risk_accepted: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.RISK_ACCEPTED',
      planned: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.PLANNED',
      comment: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.COMMENT',
      custom: 'COMPLIANCE_AUDIT.CHECK.NOTES.STATUS.CUSTOM',
    }[latest.status]

    return messageId ? intl.formatMessage({ id: messageId, defaultMessage: latest.status }) : latest.status
  }

  return latest.note ?? null
}

function ComplianceAuditTable({ tableData, print, columnConfig }) {
  const intl = useIntl()
  const visibleColumnIds = columnConfig?.visibleColumnIds ?? DEFAULT_VISIBLE_COLUMN_IDS
  const notesDisplayMode = columnConfig?.notesDisplayMode ?? 'fullText'
  const showComplianceColumn = Boolean(columnConfig?.showComplianceColumn)
  const complianceColumnLabel =
    columnConfig?.complianceColumnLabel || intl.formatMessage({ id: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE' })
  const include = (columnId) => {
    if (columnId === 'compliance') {
      return showComplianceColumn
    }

    return visibleColumnIds.includes(columnId)
  }

  const formatNameColumn = (cell) => <span className="Uic-ComplianceAuditTable-securityCheckCell">{cell}</span>

  const formatStatusColumn = (cell, row) =>
    cell ? (
      <div className="Uic-ComplianceAuditTable-statusColumn">
        <div>
          <Badge
            variant={getComplianceAuditStatusBadgeVariant(cell, intl)}
            className="Uic-SecurityPostureTable-complianceBadge"
          >
            {cell}
          </Badge>
        </div>
        {include('statusDetails') ? (
          <div className="Uic-ComplianceAuditTable-statusDetailsCell">{getPostureStatusDetails(row, intl)}</div>
        ) : null}
      </div>
    ) : null

  const formatTemplatesColumn = (cell, row) => formatComplianceAuditTemplates(row.formattedTemplates)

  const formatSecureScoreColumn = (cell, row) => {
    const { msSecureScore } = row

    if (msSecureScore?.currentScore == null) {
      return null
    }

    if (msSecureScore.maxScore != null) {
      return <SecurityPostureSecureScore {...msSecureScore} />
    }

    return <span>{msSecureScore.currentScore}</span>
  }

  const columns = useMemo(() => {
    const allColumns = [
      {
        dataField: 'formattedComplianceRef',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.REF_NUMBER' }),
        headerStyle: () => ({ width: '8%' }),
        columnId: 'compliance',
      },
      {
        dataField: 'formattedCompliance',
        text: complianceColumnLabel,
        headerStyle: () => ({ width: '18%' }),
        columnId: 'compliance',
      },
      {
        dataField: 'formattedName',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.SECURITY_CHECK' }),
        formatter: formatNameColumn,
        sort: !print,
        headerStyle: () => ({ width: '24%' }),
        always: true,
      },
      {
        dataField: 'formattedStatus',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.STATUS' }),
        formatter: formatStatusColumn,
        sort: !print,
        headerStyle: () => ({ width: '20%' }),
        always: true,
      },
      {
        dataField: 'description',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.RATIONALE' }),
        formatter: (_, row) => getPostureRationale(row, intl),
        headerStyle: () => ({ width: '28%' }),
        columnId: 'rationale',
      },
      {
        dataField: 'category',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.CATEGORIES' }),
        sort: !print,
        headerStyle: () => ({ width: '10%' }),
        columnId: 'categories',
      },
      {
        dataField: 'portal',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.PORTAL' }),
        formatter: (cell) => (Array.isArray(cell) ? cell.join(', ') : cell),
        headerStyle: () => ({ width: '10%' }),
        columnId: 'portal',
      },
      {
        dataField: 'formattedTemplates',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.TEMPLATES' }),
        formatter: formatTemplatesColumn,
        headerStyle: () => ({ width: '10%' }),
        columnId: 'assessments',
      },
      {
        dataField: 'license',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.M365_LICENSE_REQ' }),
        headerStyle: () => ({ width: '10%' }),
        columnId: 'license',
      },
      {
        dataField: 'msSecureScore.currentScore',
        text: intl.formatMessage({ id: 'COMPLIANCE_AUDIT.SECURE_SCORE' }),
        formatter: formatSecureScoreColumn,
        sort: !print,
        headerStyle: () => ({ width: '8%' }),
        columnId: 'secureScore',
      },
      {
        dataField: 'notes',
        text: intl.formatMessage({ id: 'GENERAL.NOTES' }),
        formatter: (_, row) => formatNotesCell(row.notes, notesDisplayMode, intl),
        headerStyle: () => ({ width: '16%' }),
        columnId: 'notes',
      },
    ]

    return allColumns.filter((column) => column.always || include(column.columnId))
  }, [intl, print, visibleColumnIds, notesDisplayMode, showComplianceColumn, complianceColumnLabel]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="Uic-ComplianceAuditTable-tableWrapper">
      <Table data={tableData} keyField="rowId" columns={columns} hideHeader hidePagination readOnly={print} />
    </div>
  )
}

ComplianceAuditTable.propTypes = propTypes

export default memo(ComplianceAuditTable)
